import React, { useState } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useIsMobile, merge } from "../hooks/useIsMobile";
import { useJudgeTiming } from "../hooks/useJudgeTiming";
import { isStagingFeature } from "../utils/stagingFeatures";
import CaseFlowDiagram from "../components/CaseFlowDiagram";
import CaseFlowArt from "../components/CaseFlowArt";
import CaseFlowCards from "../components/CaseFlowCards";

const COLORS = {
  bg: "#F5F5F2",
  card: "#FFFFFF",
  ink: "#111214",
  soft: "#3A3C40",
  muted: "#7A7C82",
  blue: "#2D7FF9",
  border: "rgba(17,18,20,0.09)",
  borderMd: "rgba(17,18,20,0.13)",
};

const SANS = "'Plus Jakarta Sans', sans-serif";
const MONO = "'DM Mono', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";
// Two takes on the case flow that are still experimental, closing the page:
// animated diagram and illustrated artwork. Enabled on local + staging via
// REACT_APP_STAGING_FEATURES; production never receives that var (CI does not
// pass it as a build-arg), so both stay off there. The card version graduated
// to production and always renders (see CaseFlowCards below).
const ENABLE_CASE_FLOW = isStagingFeature("case-flow");
const ENABLE_CASE_FLOW_ART = isStagingFeature("case-flow-art");

const USE_CASE_OPTIONS = [
  { value: "everyday", label: "Personal dispute or disagreement" },
  { value: "community", label: "Online community or forum conflict" },
  { value: "platform", label: "Platform or moderation disagreement" },
  { value: "strategic", label: "Strategic or business disagreement" },
  { value: "team", label: "Team or leadership decision" },
  { value: "debate", label: "A debate or contested position" },
  { value: "other", label: "Something else" },
];

function getFaqs(judgeTiming) {
  return [
    { q: "What kinds of cases is disputefai designed for?", a: "disputefai is built for any situation where two sides hold different positions and want a structured, neutral resolution. That includes personal disputes and disagreements, community and platform conflicts, debates, and strategic or business decisions where two parties or teams need an independent perspective to move forward." },
    { q: "Is disputefai's decision binding?", a: "No. disputefai is not a court, not legal arbitration, and not binding on either party. It is an independent, evidence-based assessment of both positions. What both parties choose to do with that assessment is entirely up to them. Many users find that a clear, neutral analysis is enough to break a deadlock — even without any formal obligation to act on it." },
    { q: "Does disputefai keep the information and evidence I upload private and confidential?", a: "Yes. Case submissions — including everything both sides share — are private by default. The only people who receive the full case record and written resolution are the two participating parties. disputefai does not publish, share, or expose case content to any third party. During the beta, we may review case submissions internally to evaluate how the system is performing, but we handle all submissions with care and do not share individual case details." },
    { q: "Does the disputefai AI use the information I submit to train its models?", a: "No. The information you submit to disputefai is not used to train the underlying AI models. disputefai uses existing frontier AI models — Claude, GPT-4, and Gemini — via their APIs under standard terms that do not permit submitted content to be used for model training. Your case content is used solely to generate your resolution." },
    { q: "What happens if the other side doesn't respond?", a: `The other party has ${judgeTiming.label} to accept the invitation and submit their perspective. If they don't respond within that window, the case closes and the person who opened it is notified. The case does not proceed to a resolution without both sides participating — that is fundamental to how disputefai works. A resolution based on one side only would defeat the purpose.` },
    { q: "How is this different from just asking ChatGPT or another AI?", a: "When you ask a standard AI about a conflict or decision, it only receives your version of events and your framing. It has no way to hear the other side. disputefai is structurally different: both parties submit independently, through the same process, and the AI reviews the complete record before reaching any conclusion. The resolution reflects what both sides actually said — not just the version of whoever asked first." },
    { q: "What can I submit as evidence? Can witnesses be included?", a: "Both parties can upload files, photos, screenshots, and documents to support their position — anything that helps substantiate their account. Third parties can also be included in the process as witnesses. Witnesses submit their account directly, and it becomes part of the full record the AI panel reviews alongside both primary positions. Including strong evidence and relevant witnesses generally leads to a more grounded and well-reasoned resolution." },
  ];
}

export default function HomeBeta() {
  const isMobile = useIsMobile();
  const [openFaq, setOpenFaq] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", use_case: "", context: "" });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/beta-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={s.page}>
      <SiteHeader />
      <main>
        <HeroSection isMobile={isMobile} />
        <CaseExampleSection />
        <NotForSection />
        <FormSection
          isMobile={isMobile}
          submitted={submitted}
          submitting={submitting}
          submitError={submitError}
          form={form}
          onChange={onChange}
          onSubmit={onSubmit}
        />
        <FaqSection openFaq={openFaq} setOpenFaq={setOpenFaq} />
        {ENABLE_CASE_FLOW && <CaseFlowDiagram />}
        {ENABLE_CASE_FLOW_ART && <CaseFlowArt />}
        <CaseFlowCards />
      </main>
      <SiteFooter />
    </div>
  );
}

// ── Hero (blue gradient backdrop) ────────────────────────────────────────────
function HeroSection({ isMobile }) {
  return (
    <section style={merge(s.heroBg, s.heroBgMobile, isMobile)}>
      <div style={s.wrap}>
        <div style={merge(s.heroCard, s.heroCardMobile, isMobile)}>
          <div style={s.heroTag}>Experimental · Limited beta</div>
          <h1 style={s.h1}>Two-sided AI resolution. For disputes, disagreements, and decisions.</h1>
          <p style={s.heroSub}>
            disputefai is a limited beta. Two sides present their position. An independent AI
            reviews the full record and delivers a structured, evidence-based resolution — for
            conflicts, strategic disagreements, and decisions that need a neutral perspective.
          </p>
          <a href="#apply" style={{ ...s.heroCta, ...s.heroCtaCentered }}>
            Request Beta Access
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Case example (walkthrough of a real beta case) ──────────────────────────
function CaseAnnotation({ num, children }) {
  return (
    <>
      <div style={s.caseAnnotation}>
        <span style={s.caseAnnNum}>{num}</span>
        <span style={s.caseAnnText}>{children}</span>
      </div>
      <div style={s.caseConnector} />
    </>
  );
}

function CaseExampleSection() {
  return (
    <section style={s.caseSection}>
      <div style={s.wrap}>
        <span style={{ ...s.label, marginBottom: 6 }}>How it works — a real example</span>
        <h2 style={s.caseH2}>See an actual case from start to finish.</h2>
        <p style={s.caseIntro}>
          This is a real case submitted during beta testing. Here is how the process unfolds
          from first submission to decision.
        </p>

        <CaseAnnotation num="01">
          One side opens a case — a dispute, disagreement, or decision with two positions.
        </CaseAnnotation>
        <div style={s.caseCard}>
          <div style={s.caseCardHead}>
            <span style={s.caseCardType}>Dispute</span>
            <div style={s.caseCardTitle}>Invest in stock market or not</div>
            <div style={s.caseCardMeta}>
              <span style={s.caseMetaBy}>Started by <strong style={s.strong}>Scott M.</strong></span>
              <span style={s.caseTag}>Other</span>
              <span style={s.caseTag}>Massachusetts</span>
            </div>
          </div>
          <div style={s.caseCardBody}>
            <span style={s.caseFiled}>Filed 7/28/2026</span>
            <span style={{ ...s.caseFieldLabel, marginTop: 0 }}>Testimony</span>
            <p style={s.caseFieldText}>
              I believe my wife and I should invest in the stock market now with some extra
              money we have. It will go into a non-weighted S&amp;P index fund so that it is
              extra safe. If we don't invest the money and leave it in cash, we'll be losing
              money to inflation. We have another 30 years to live, so we need the money to
              continue to appreciate. The market may go down, but over the next 30 years it
              will surely rebound and go up.
            </p>
            <span style={s.caseFieldLabel}>Desired outcome</span>
            <p style={s.caseFieldOutcome}>Agreement to invest in the stock market.</p>
          </div>
        </div>

        <CaseAnnotation num="02">The other side responds with their position.</CaseAnnotation>
        <div style={s.caseCard}>
          <div style={{ ...s.caseCardHead, background: "rgba(17,18,20,0.02)" }}>
            <span style={s.caseCardType}>Response from Disputant · Turn 2</span>
            <div style={s.caseCardMeta}>
              <span style={s.caseMetaBy}>Responded <strong style={s.strong}>7/28/2026</strong></span>
            </div>
          </div>
          <div style={s.caseCardBody}>
            <span style={{ ...s.caseFieldLabel, marginTop: 0 }}>Testimony</span>
            <p style={s.caseFieldText}>
              My husband wants to take some of the extra cash we have and invest it into the
              stock market. I don't think that's a good idea because the stock market is at a
              record high. Many advisors are saying it could crash and then we'd have lost
              money that we could use for something else. I understand he wants to put it into
              an equal weighted fund but even that will go down if there is a crash. Why don't
              we wait and see how things shake out for the next year or two and then make a
              decision.
            </p>
            <span style={s.caseFieldLabel}>Desired outcome</span>
            <p style={s.caseFieldOutcome}>
              Agree to keep money safe for now until we see how the stock market performs over
              the next year or two.
            </p>
          </div>
        </div>

        <CaseAnnotation num="03">
          The AI considers both submissions and provides a neutral decision.
        </CaseAnnotation>
        <div style={s.verdictCard}>
          <div style={s.verdictHead}>
            <span style={s.verdictTag}>Panel verdict — reviewed by Claude, GPT-4, and Gemini</span>
            <div style={s.verdictTitle}>The Strategy for Surplus Capital Investment</div>
          </div>
          <div style={s.verdictBody}>
            <div style={s.verdictSection}>
              <div style={s.verdictSectionTitle}>Where the panel agreed</div>
              <div style={s.verdictSectionText}>
                All three reviews agree that holding funds in cash over a 30-year horizon is
                unnecessarily risky due to inflation. None of the reviewers supported a
                complete, indefinite hold — "timing the market" is notoriously difficult and
                costly.
              </div>
            </div>
            <hr style={s.verdictDivider} />
            <div style={s.verdictSection}>
              <div style={s.verdictSectionTitle}>Final ruling</div>
              <div style={s.verdictSectionText}>
                The claimant prevails in the objective to shift from cash to an
                investment-based strategy. However, to respect the disputant's valid concerns
                about market volatility, the panel directs the couple to adopt a Dollar-Cost
                Averaging (DCA) strategy — deploying funds in equal increments over the next 12
                to 24 months. This ensures funds begin working immediately while mitigating the
                risk of a lump-sum entry at a market peak.
              </div>
            </div>
            <hr style={s.verdictDivider} />
            <div style={{ ...s.verdictSection, marginBottom: 0 }}>
              <div style={s.verdictSectionTitle}>In plain terms</div>
              <div style={s.verdictSectionText}>
                The husband wins the argument that the money must be invested, but both sides
                win the method: by investing in smaller chunks over the next year or two, you
                reduce the risk of a market crash while ensuring your money doesn't lose value
                to inflation.
              </div>
            </div>
          </div>
        </div>

        <div style={s.caseCtaWrap}>
          <a href="#apply" style={s.caseCta}>
            Have a dispute or disagreement? Try disputefai
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Not for ─────────────────────────────────────────────────────────────────
function NotForSection() {
  return (
    <section style={s.section}>
      <div style={s.wrap}>
        <span style={s.label}>Important</span>
        <div style={s.notFor}>
          <strong style={s.strong}>disputefai is not a court, not legal advice, not therapy, and
          not binding.</strong> It is not for emergencies, threats, abuse, domestic violence,
          criminal matters, child safety issues, or any situation requiring professional help.
        </div>
      </div>
    </section>
  );
}

// ── Form ────────────────────────────────────────────────────────────────────
function FormSection({ isMobile, submitted, submitting, submitError, form, onChange, onSubmit }) {
  return (
    <section id="apply" style={s.formSection}>
      <div style={s.wrap}>
        <h2 style={s.h2}>Request access to the beta.</h2>
        <p style={s.formSub}>
          We're testing two-sided AI resolution with real cases. Fill in the form and we'll
          review your request and follow up by email.
        </p>

        <div style={s.formCard}>
          {!submitted && (
            <form onSubmit={onSubmit}>
              <div style={s.fg}>
                <div style={merge(s.formRow, s.formRowMobile, isMobile)}>
                  <div>
                    <label style={s.label2} htmlFor="fname">First name</label>
                    <input style={s.input} type="text" id="fname" name="first_name" placeholder="Alex" value={form.first_name} onChange={onChange} required />
                  </div>
                  <div>
                    <label style={s.label2} htmlFor="lname">Last name</label>
                    <input style={s.input} type="text" id="lname" name="last_name" placeholder="Rivera" value={form.last_name} onChange={onChange} required />
                  </div>
                </div>
              </div>

              <div style={s.fg}>
                <label style={s.label2} htmlFor="email">Email address</label>
                <input style={s.input} type="email" id="email" name="email" placeholder="alex@example.com" value={form.email} onChange={onChange} required />
              </div>

              <div style={s.fg}>
                <label style={s.label2} htmlFor="use-case">What kind of dispute are you likely to bring?</label>
                <select style={s.select} id="use-case" name="use_case" value={form.use_case} onChange={onChange} required>
                  <option value="" disabled>Select one</option>
                  {USE_CASE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ ...s.fg, marginBottom: 0 }}>
                <label style={s.label2} htmlFor="context">
                  Tell us about it <span style={s.opt}>Optional</span>
                </label>
                <textarea style={s.textarea} id="context" name="context" placeholder="Briefly describe the dispute you have in mind." value={form.context} onChange={onChange} />
              </div>

              {submitError && <p style={s.formError}>{submitError}</p>}

              <button type="submit" style={s.submitBtn} disabled={submitting}>
                {submitting ? "Submitting…" : "Request Beta Access"}
              </button>
              <p style={s.formFine}>
                Decisions are advisory and do not constitute legal advice. This is an
                experimental product in active development.
              </p>
            </form>
          )}

          {submitted && (
            <div style={s.formSuccess}>
              <div style={s.formSuccessIcon}>✅</div>
              <h3 style={s.formSuccessH3}>Request received.</h3>
              <p style={s.formSuccessP}>
                We review beta requests by hand — you'll get an email once your account is
                approved. Thank you for helping us test two-sided AI resolution.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────────
function FaqSection({ openFaq, setOpenFaq }) {
  const judgeTiming = useJudgeTiming();
  const FAQS = getFaqs(judgeTiming);
  return (
    <section style={s.faqSection}>
      <div style={s.wrap}>
        <h2 style={s.faqH2}>Common questions</h2>
        <div>
          {FAQS.map((f, i) => (
            <div key={i} style={merge(s.faqItem, s.faqItemLast, i === FAQS.length - 1)}>
              <button style={s.faqSummary} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{f.q}</span>
                <span style={merge(s.faqIcon, s.faqIconOpen, openFaq === i)}>+</span>
              </button>
              {openFaq === i && <div style={s.faqAnswer}>{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const s = {
  // Flex column so SiteFooter's `marginTop: auto` pins it to the bottom of the
  // viewport on short pages — same shell as pageStyles.root.
  page: { background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, WebkitFontSmoothing: "antialiased", minHeight: "100vh", display: "flex", flexDirection: "column" },
  wrap: { maxWidth: 600, margin: "0 auto", padding: "0 28px" },

  // Hero: white card on a full-width blue gradient backdrop
  heroBg: {
    // Gray palette (kept for reference): "linear-gradient(135deg, #2B2E33 0%, #9AA0AA 50%, #2B2E33 100%)"
    background: "linear-gradient(135deg, #5C87E0 0%, #A9C4F5 50%, #5C87E0 100%)",
    padding: "72px 0",
  },
  heroBgMobile: { padding: "40px 0" },
  heroCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: "36px 34px",
    boxShadow: "0 24px 60px rgba(17,18,20,0.28)",
  },
  heroCardMobile: { padding: "26px 22px" },
  heroTag: { fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#1A5EC4", marginBottom: 20, display: "flex", alignItems: "center", gap: 8 },
  h1: { fontSize: "clamp(28px, 5vw, 40px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1, color: COLORS.ink, marginBottom: 18, marginTop: 0 },
  heroSub: { fontSize: 16, color: COLORS.soft, lineHeight: 1.7, marginBottom: 28 },
  heroCta: { display: "inline-flex", alignItems: "center", gap: 7, padding: "12px 22px", background: "#1A5EC4", color: "#fff", borderRadius: 8, fontSize: 14.5, fontWeight: 700, textDecoration: "none", letterSpacing: "-0.01em" },
  heroCtaCentered: { display: "flex", width: "fit-content", margin: "0 auto" },

  // Sections
  section: { padding: "44px 0", borderBottom: `1px solid ${COLORS.border}` },
  label: { fontFamily: MONO, fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "#1A5EC4", marginBottom: 20, display: "block" },

  strong: { fontWeight: 600, color: COLORS.ink },

  // Case example
  caseSection: { padding: "52px 0", borderBottom: `1px solid ${COLORS.border}` },
  caseH2: { fontSize: "clamp(19px, 2.8vw, 22px)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.18, color: COLORS.ink, marginTop: 0, marginBottom: 6 },
  caseIntro: { fontSize: 14, color: COLORS.soft, marginTop: 0, marginBottom: 32, lineHeight: 1.6 },
  caseAnnotation: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 },
  caseAnnNum: { width: 24, height: 24, borderRadius: "50%", background: "#1A5EC4", color: "#fff", fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, fontFamily: MONO },
  caseAnnText: { fontSize: 14, fontWeight: 600, color: "#1A5EC4", lineHeight: 1.45, letterSpacing: "-0.01em", paddingTop: 3 },
  caseConnector: { width: 2, height: 12, background: "#1A5EC4", marginLeft: 11, opacity: 0.4 },
  caseCard: { background: COLORS.card, border: `1.5px solid ${COLORS.borderMd}`, borderRadius: 10, overflow: "hidden", marginBottom: 24 },
  caseCardHead: { padding: "12px 16px 10px", borderBottom: `1px solid ${COLORS.border}` },
  caseCardType: { fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase", color: COLORS.soft, marginBottom: 5, display: "block" },
  caseCardTitle: { fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em", color: COLORS.ink, lineHeight: 1.2, marginBottom: 8 },
  caseCardMeta: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  caseMetaBy: { fontSize: 12, color: COLORS.soft },
  caseTag: { fontSize: 10.5, fontWeight: 500, color: COLORS.soft, background: "rgba(17,18,20,0.06)", border: `1px solid ${COLORS.borderMd}`, borderRadius: 4, padding: "1px 7px" },
  caseCardBody: { padding: "14px 16px" },
  caseFiled: { fontSize: 11, color: COLORS.soft, fontFamily: MONO, marginBottom: 10, display: "block" },
  caseFieldLabel: { fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: COLORS.soft, marginTop: 12, marginBottom: 6, display: "block" },
  caseFieldText: { fontSize: 13.5, color: COLORS.soft, lineHeight: 1.65, margin: 0 },
  caseFieldOutcome: { fontSize: 13.5, fontWeight: 600, color: COLORS.ink, lineHeight: 1.55, margin: 0 },

  // Case example — verdict card
  verdictCard: { background: "#FEFAEE", border: "1.5px solid rgba(200,160,40,0.25)", borderRadius: 10, overflow: "hidden", marginBottom: 24 },
  verdictHead: { padding: "11px 16px", borderBottom: "1px solid rgba(200,160,40,0.2)" },
  verdictTag: { fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7A6010", display: "block", marginBottom: 4 },
  verdictTitle: { fontSize: 15, fontWeight: 700, color: COLORS.ink, letterSpacing: "-0.015em" },
  verdictBody: { padding: "14px 16px" },
  verdictSection: { marginBottom: 14 },
  verdictSectionTitle: { fontSize: 12, fontWeight: 700, color: COLORS.ink, marginBottom: 5, letterSpacing: "-0.01em" },
  verdictSectionText: { fontSize: 13, color: COLORS.soft, lineHeight: 1.65 },
  verdictDivider: { border: "none", borderTop: "1px solid rgba(200,160,40,0.2)", margin: "12px 0" },
  caseCtaWrap: { textAlign: "center", marginTop: 4, marginBottom: 8 },
  caseCta: { display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 24px", background: "#1A5EC4", color: "#fff", borderRadius: 8, fontFamily: SANS, fontSize: 15, fontWeight: 700, textDecoration: "none", letterSpacing: "-0.01em" },

  // Not for
  notFor: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "18px 20px", fontSize: 13.5, color: COLORS.soft, lineHeight: 1.7 },

  // Form
  formSection: { padding: "52px 0 64px" },
  h2: { fontSize: "clamp(22px, 3.5vw, 28px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.15, marginBottom: 8, marginTop: 0 },
  formSub: { fontSize: 15, color: COLORS.soft, marginBottom: 28, lineHeight: 1.6, marginTop: 0 },
  formCard: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "28px 26px" },
  formRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  formRowMobile: { gridTemplateColumns: "1fr" },
  fg: { marginBottom: 14 },
  label2: { display: "block", fontSize: 12.5, fontWeight: 600, color: COLORS.ink, marginBottom: 6, letterSpacing: "0.01em" },
  opt: { fontWeight: 400, color: COLORS.muted },
  input: { width: "100%", padding: "10px 13px", border: `1.5px solid ${COLORS.borderMd}`, borderRadius: 7, fontFamily: SANS, fontSize: 14.5, color: COLORS.ink, background: COLORS.bg, outline: "none", boxSizing: "border-box" },
  select: { width: "100%", padding: "10px 13px", border: `1.5px solid ${COLORS.borderMd}`, borderRadius: 7, fontFamily: SANS, fontSize: 14.5, color: COLORS.ink, background: COLORS.bg, outline: "none", boxSizing: "border-box", cursor: "pointer" },
  textarea: { width: "100%", padding: "10px 13px", border: `1.5px solid ${COLORS.borderMd}`, borderRadius: 7, fontFamily: SANS, fontSize: 14.5, color: COLORS.ink, background: COLORS.bg, outline: "none", boxSizing: "border-box", resize: "vertical", minHeight: 86, lineHeight: 1.55 },
  submitBtn: { width: "100%", padding: 13, background: "#1A5EC4", color: "#fff", border: "none", borderRadius: 8, fontFamily: SANS, fontSize: 15.5, fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", marginTop: 16 },
  formError: { fontSize: 12.5, color: "#B3261E", marginTop: 12, marginBottom: 0 },
  formFine: { fontSize: 11.5, color: COLORS.muted, textAlign: "center", marginTop: 12, lineHeight: 1.5 },
  formSuccess: { textAlign: "center", padding: "40px 20px" },
  formSuccessIcon: { fontSize: 32, marginBottom: 14 },
  formSuccessH3: { fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 8, marginTop: 0 },
  formSuccessP: { fontSize: 14.5, color: COLORS.soft, lineHeight: 1.6, margin: 0 },

  // FAQ
  faqSection: { padding: "52px 0 64px", borderTop: `1px solid ${COLORS.border}` },
  faqH2: { fontSize: "clamp(20px, 3vw, 24px)", fontWeight: 800, letterSpacing: "-0.025em", marginBottom: 28, marginTop: 0 },
  faqItem: { borderTop: `1px solid ${COLORS.border}` },
  faqItemLast: { borderBottom: `1px solid ${COLORS.border}` },
  faqSummary: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "16px 0", cursor: "pointer", gap: 20, fontSize: 15, fontWeight: 600, color: COLORS.ink, letterSpacing: "-0.01em", lineHeight: 1.4, width: "100%", background: "transparent", border: "none", fontFamily: SANS, textAlign: "left" },
  faqIcon: { width: 22, height: 22, flexShrink: 0, borderRadius: "50%", background: "rgba(17,18,20,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 300, marginTop: 1, color: COLORS.ink, transition: "transform 0.2s, background 0.15s" },
  faqIconOpen: { transform: "rotate(45deg)", background: COLORS.ink, color: "#fff" },
  faqAnswer: { padding: "0 32px 18px 0", fontSize: 14.5, color: COLORS.soft, lineHeight: 1.7, maxWidth: "58ch" },
};
